import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Clock, MapPin, Wifi, ArrowLeft, Timer, AlertCircle, RefreshCw } from "lucide-react";
import { useServerTime } from "@/hooks/useServerTime";
import { useNavigate } from "react-router-dom";

interface TimeEntry {
  id: string;
  employee_id: string;
  clock_in_time: string;
  clock_out_time: string | null;
  total_hours: number | null;
}

interface Employee {
  id: string;
  employee_id: string;
  first_name: string;
  last_name: string;
}

export const StaffEmployeeClockIn = () => {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [activeEntry, setActiveEntry] = useState<TimeEntry | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [location, setLocation] = useState<GeolocationPosition | null>(null);
  const [networkStatus, setNetworkStatus] = useState<"checking" | "verified" | "failed">("checking");
  const [employeeError, setEmployeeError] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { 
    serverTime, 
    isLoading: timeLoading, 
    error: timeError, 
    calculateTimeWorked, 
    formatDuration, 
    isTimeSynced,
    syncTime 
  } = useServerTime();

  useEffect(() => {
    checkNetworkStatus();
    getCurrentLocation();
    lookupEmployeeByPhone();
  }, []);

  const checkNetworkStatus = async () => {
    setNetworkStatus("checking");
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      const userIP = data.ip;
      
      const isValidIP = userIP.startsWith("173.174");
      setNetworkStatus(isValidIP ? "verified" : "failed");
      
      if (!isValidIP) {
        toast({
          title: "Network Check Failed",
          description: "You must be connected to the store network to clock in/out.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Network check failed:', error);
      setNetworkStatus("failed");
      toast({
        title: "Network Check Failed",
        description: "Unable to verify network connection.",
        variant: "destructive"
      });
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation(position);
        },
        (error) => {
          console.error('Location error:', error);
          toast({
            title: "Location Access Required",
            description: "Please enable location access to clock in/out.",
            variant: "destructive"
          });
        }
      );
    }
  };

  const lookupEmployeeByPhone = async () => {
    // Get phone number from SMS auth
    const smsAuth = localStorage.getItem('staff_sms_auth');
    if (!smsAuth) {
      setEmployeeError("SMS authentication not found. Please log in again.");
      return;
    }

    let phoneNumber;
    try {
      const authData = JSON.parse(smsAuth);
      phoneNumber = authData.phoneNumber;
    } catch {
      setEmployeeError("Invalid SMS authentication data. Please log in again.");
      return;
    }
    
    setIsLoading(true);
    setEmployeeError(null);
    
    try {
      // Use the RPC function that handles phone format conversion
      const { data: validationResult, error: rpcError } = await supabase
        .rpc('validate_employee_phone', { input_phone: phoneNumber });

      if (rpcError) {
        console.error('Employee validation RPC error:', rpcError);
        setEmployeeError("Failed to validate phone number. Please try again.");
        return;
      }

      if (!validationResult || !Array.isArray(validationResult) || validationResult.length === 0) {
        console.error('Employee validation failed:', validationResult);
        setEmployeeError("No active employee record found for your phone number. Please contact your administrator.");
        return;
      }

      const result = validationResult[0];
      if (!result.is_valid || !result.employee_data) {
        console.error('Employee validation failed:', result);
        setEmployeeError("No active employee record found for your phone number. Please contact your administrator.");
        return;
      }

      const employeeData = result.employee_data as any;
      const employee: Employee = {
        id: employeeData.id,
        employee_id: employeeData.employee_id,
        first_name: employeeData.first_name,
        last_name: employeeData.last_name
      };
      
      setEmployee(employee);

      // Look up active time entry
      const { data: activeEntryData } = await supabase
        .from('time_entries')
        .select('*')
        .eq('employee_id', employee.id)
        .is('clock_out_time', null)
        .order('clock_in_time', { ascending: false })
        .limit(1);

      if (activeEntryData && activeEntryData.length > 0) {
        setActiveEntry(activeEntryData[0] as TimeEntry);
      } else {
        setActiveEntry(null);
      }

    } catch (error) {
      console.error('Employee lookup error:', error);
      setEmployeeError("Failed to lookup employee information. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const clockIn = async () => {
    if (!employee || !location || networkStatus !== "verified") {
      toast({
        title: "Cannot Clock In",
        description: "Network verification and location access required.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const ipData = await response.json();

      const { data, error } = await supabase
        .rpc('clock_in_employee', {
          emp_id: employee.id,
          clock_in_location_data: {
            latitude: location.coords.latitude,
            longitude: location.coords.longitude,
            accuracy: location.coords.accuracy
          },
          clock_in_ip_address: ipData.ip
        });

      if (error) {
        console.error('Clock in error:', error);
        throw error;
      }

      setActiveEntry(data as TimeEntry);
      toast({
        title: "Clocked In Successfully",
        description: `Welcome ${employee.first_name}! You are now clocked in.`,
      });

    } catch (error) {
      console.error('Clock in error:', error);
      toast({
        title: "Clock In Failed",
        description: "Failed to clock in. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clockOut = async () => {
    if (!employee || !activeEntry || !location || networkStatus !== "verified") {
      toast({
        title: "Cannot Clock Out",
        description: "Network verification and location access required.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const ipData = await response.json();

      const { error } = await supabase
        .rpc('clock_out_employee', {
          time_entry_id: activeEntry.id,
          clock_out_location_data: {
            latitude: location.coords.latitude,
            longitude: location.coords.longitude,
            accuracy: location.coords.accuracy
          },
          clock_out_ip_address: ipData.ip
        });

      if (error) {
        console.error('Clock out error:', error);
        throw error;
      }

      setActiveEntry(null);
      toast({
        title: "Clocked Out Successfully",
        description: `Goodbye ${employee.first_name}! You have been clocked out.`,
      });

    } catch (error) {
      console.error('Clock out error:', error);
      toast({
        title: "Clock Out Failed",
        description: "Failed to clock out. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/staff-dashboard');
  };

  // Calculate current time worked with validation
  const timeWorked = activeEntry && isTimeSynced ? calculateTimeWorked(activeEntry.clock_in_time) : null;
  const isOvertime = timeWorked ? timeWorked.totalHours > 8 : false;

  return (
    <div className="max-w-screen-md mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button 
          onClick={handleBack} 
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <Clock className="w-6 h-6 text-muted-foreground" />
        <div>
          <h1 className="text-xl font-semibold">Employee Clock In / Clock Out</h1>
          <p className="text-sm text-muted-foreground">Track your shift start and end times</p>
        </div>
      </div>

      {/* System Status Cards */}
      <div className="grid gap-3 sm:gap-4 grid-cols-3">
        <Card>
          <CardContent className="flex items-center justify-center p-3 sm:p-4">
            <div className="text-center">
              <Wifi className={`w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1 sm:mb-2 ${
                networkStatus === "verified" ? "text-green-500" : 
                networkStatus === "failed" ? "text-destructive" : "text-yellow-500"
              }`} />
              <p className="text-xs sm:text-sm font-medium">Network</p>
              <p className="text-xs text-muted-foreground capitalize">{networkStatus}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center justify-center p-3 sm:p-4">
            <div className="text-center">
              <MapPin className={`w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1 sm:mb-2 ${
                location ? "text-green-500" : "text-destructive"
              }`} />
              <p className="text-xs sm:text-sm font-medium">Location</p>
              <p className="text-xs text-muted-foreground">{location ? "Verified" : "Required"}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center justify-center p-3 sm:p-4">
            <div className="text-center">
              <Timer className={`w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1 sm:mb-2 ${
                activeEntry ? (isOvertime ? "text-destructive" : "text-green-500") : "text-muted-foreground"
              }`} />
              <p className="text-xs sm:text-sm font-medium">Status</p>
              <p className="text-xs text-muted-foreground">{activeEntry ? "Clocked In" : "Clocked Out"}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Current Shift Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-muted-foreground" />
            Current Shift Status
          </CardTitle>
          <p className="text-sm text-muted-foreground">Updated in real time</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Server Time */}
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-sm font-medium">Server Time</span>
              {!isTimeSynced && (
                <Button
                  onClick={syncTime}
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                >
                  <RefreshCw className="w-3 h-3" />
                </Button>
              )}
            </div>
            {timeLoading ? (
              <div className="text-muted-foreground text-sm">Syncing...</div>
            ) : timeError ? (
              <div className="text-destructive text-sm">Time sync error - using local time</div>
            ) : (
              <div className="text-xl font-mono font-semibold">
                {serverTime.toLocaleTimeString()}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-1">
              {serverTime.toLocaleDateString()}
            </div>
            {!isTimeSynced && (
              <div className="text-xs text-yellow-600 mt-1">
                Using local time (server sync failed)
              </div>
            )}
          </div>

          {/* Active Session Timer */}
          {activeEntry && (
            <div className="text-center p-4 border rounded-lg">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Timer className={`w-5 h-5 ${isOvertime ? "text-destructive" : "text-green-500"}`} />
                <span className="font-medium">Time Worked Today</span>
              </div>
              {!isTimeSynced ? (
                <div className="text-yellow-600 text-sm">
                  Timer paused - syncing time...
                </div>
              ) : timeWorked ? (
                <>
                  <div className={`text-3xl font-mono font-bold ${isOvertime ? "text-destructive" : "text-green-600"}`}>
                    {formatDuration(timeWorked.hours, timeWorked.minutes, timeWorked.seconds)}
                  </div>
                  {isOvertime && (
                    <div className="flex items-center justify-center gap-1 mt-2 text-destructive">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Overtime ({timeWorked.totalHours.toFixed(1)} hours total)</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-muted-foreground">Calculating...</div>
              )}
              <div className="text-sm text-muted-foreground mt-2">
                Started at: {new Date(activeEntry.clock_in_time).toLocaleTimeString()}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employee Actions or Error */}
      {employeeError ? (
        <Card>
          <CardContent className="flex items-center justify-center p-6">
            <div className="text-center space-y-3">
              <AlertCircle className="w-12 h-12 text-destructive mx-auto" />
              <div>
                <h3 className="font-semibold text-destructive">Employee Record Not Found</h3>
                <p className="text-sm text-muted-foreground mt-1">{employeeError}</p>
              </div>
              <Button 
                onClick={lookupEmployeeByPhone}
                disabled={isLoading}
                variant="outline"
                size="sm"
              >
                {isLoading ? "Retrying..." : "Try Again"}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : employee ? (
        <Card>
          <CardHeader>
            <CardTitle>Welcome, {employee.first_name} {employee.last_name}</CardTitle>
            <p className="text-sm text-muted-foreground">Employee ID: {employee.employee_id}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeEntry && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground text-center">
                  Clocked in at: {new Date(activeEntry.clock_in_time).toLocaleString()}
                </p>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3">
              {activeEntry ? (
                <Button 
                  onClick={clockOut}
                  disabled={isLoading || networkStatus !== "verified" || !location}
                  variant="destructive"
                  size="lg"
                  className="w-full sm:flex-1"
                >
                  {isLoading ? "Clocking Out..." : "Clock Out"}
                </Button>
              ) : (
                <Button 
                  onClick={clockIn}
                  disabled={isLoading || networkStatus !== "verified" || !location}
                  size="lg"
                  className="w-full sm:flex-1"
                >
                  {isLoading ? "Clocking In..." : "Clock In"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center p-6">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Loading your employee information...</p>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};